import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import aiService from '../../services/aiService';

const INSIGHTS_CACHE_KEY = 'zecher_ai_insights_cache';
const INSIGHTS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

const InsightFeed = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check cache first unless forcing refresh
      if (!forceRefresh) {
        const cachedData = localStorage.getItem(INSIGHTS_CACHE_KEY);
        if (cachedData) {
          try {
            const { insights: cachedInsights, timestamp } = JSON.parse(cachedData);
            const now = new Date().getTime();
            
            // Use cached data if it's less than 10 minutes old
            if (now - timestamp < INSIGHTS_CACHE_DURATION) {
              console.log('🔄 Using cached AI insights');
              setInsights(cachedInsights);
              setLoading(false);
              return;
            } else {
              console.log('⏰ Cached insights expired, fetching new ones');
            }
          } catch (cacheError) {
            console.warn('Failed to parse cached insights:', cacheError);
          }
        }
      }
      
      console.log('🤖 Fetching fresh AI insights...');
      const aiInsights = await aiService.getInsights('demo_user');
      
      // Format insights for display
      const formattedInsights = aiInsights.map(insight => 
        aiService.formatInsightForDisplay(insight)
      );
      
      setInsights(formattedInsights);
      
      // Cache the results
      const cacheData = {
        insights: formattedInsights,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(INSIGHTS_CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 AI insights cached for 10 minutes');
      
    } catch (err) {
      console.error('Error loading insights:', err);
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadInsights(true); // Force refresh
    setRefreshing(false);
  };

  const getActionButton = (insight) => {
    if (insight.priority === 'high') {
      return { text: 'Take Action', style: styles.actionButtonHigh };
    } else if (insight.priority === 'medium') {
      return { text: 'Review', style: styles.actionButtonMedium };
    } else {
      return { text: 'Learn More', style: styles.actionButtonLow };
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Financial Insights</Text>
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>🤖 Loading insights...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Financial Insights</Text>
          <TouchableOpacity onPress={handleRefresh} style={styles.refreshButton}>
            <Text style={styles.refreshButtonText}>↻ Retry</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>⚠️ {error}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Financial Insights</Text>
        <TouchableOpacity 
          onPress={handleRefresh} 
          style={styles.refreshButton}
          disabled={refreshing}
        >
          <Text style={styles.refreshButtonText}>
            {refreshing ? '↻ Refreshing...' : '↻ Refresh'}
          </Text>
        </TouchableOpacity>
      </View>
      
      <ScrollView style={styles.insightsList} showsVerticalScrollIndicator={false}>
        {insights.map((insight, index) => (
          <View key={index} style={styles.insightCard}>
            <View style={styles.insightHeader}>
              <View style={styles.insightTitleRow}>
                <Text style={styles.insightIcon}>{insight.icon}</Text>
                <Text style={styles.insightTitle}>{insight.title}</Text>
              </View>
              <View style={[
                styles.priorityBadge,
                { backgroundColor: insight.color + '20' } // 20% opacity
              ]}>
                <Text style={[styles.priorityText, { color: insight.color }]}>
                  {insight.priority}
                </Text>
              </View>
            </View>
            
            <Text style={styles.insightMessage}>{insight.message}</Text>
            
            {insight.action && (
              <View style={styles.actionSection}>
                <Text style={styles.actionLabel}>💡 Recommended Action:</Text>
                <Text style={styles.actionText}>{insight.action}</Text>
              </View>
            )}
            
            <View style={styles.insightFooter}>
              <TouchableOpacity 
                style={[styles.actionButton, getActionButton(insight).style]}
                onPress={() => console.log('Action clicked for:', insight.title)}
              >
                <Text style={styles.actionButtonText}>
                  {getActionButton(insight).text}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
        
        {insights.length === 0 && (
          <View style={styles.emptyState}>
            <Text style={styles.emptyStateText}>
              🔍 No insights available yet. Connect your accounts to get personalized recommendations!
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    height: 500,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: '500',
    color: '#111827',
  },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
  },
  refreshButtonText: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#6B7280',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#DC2626',
  },
  insightsList: {
    flex: 1,
  },
  insightCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  insightTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  insightIcon: {
    fontSize: 20,
    marginRight: 8,
  },
  insightTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  insightMessage: {
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
    marginBottom: 12,
  },
  actionSection: {
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  actionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1D4ED8',
    marginBottom: 4,
  },
  actionText: {
    fontSize: 13,
    color: '#1E40AF',
    lineHeight: 18,
  },
  insightFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  actionButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  actionButtonHigh: {
    backgroundColor: '#DC2626',
  },
  actionButtonMedium: {
    backgroundColor: '#D97706',
  },
  actionButtonLow: {
    backgroundColor: '#059669',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default InsightFeed; 